(() => {
  /**
   * Handle the devil character spine animations
   */
  class DevilCharacter extends PIXI.Container {
    constructor() {
      super();
      // asset name
      this._devilSpineName = "KrakenSpine";

      // devil spine asset
      this._devilCharacterSpine = null;

      // firewave animated sprite
      this._bubbleWaveAnimations = [];

      this._devilShootEmitterContainer = null;

      // idle loop counter for the devil(play blink anim 1 in 4 times)
      this._idleLoopCounter = 0;

      this.idle = false;
    }

    /**
     * Initialise the class
     */
    initialise() {
      messageBus.subscribe("spinner:GameConfigEvent", () => {
        this._onLayoutComplete();
      });
      Reflex.registerEventHandler("orientationChange", this._orientationChange.bind(this));
    }

    /**
     * Grab the devil character spine assets
     */
    _onLayoutComplete() {
      this.parent.visible = true;

      this._devilShootEmitterContainer = resourceManager.get("reelEffectsContainer:devilShootEmitterContainer");
      this._bubbleShoot = resourceManager.get("reelEffectsContainer:devilShootEmitterContainer:bubbleShoot");
      this._bubbleShoot.visible = false;

      this._devilCharacterSpine = resourceManager.createObject("spine", this._devilSpineName, this, {
        name: this._devilSpineName,
        defaultAnimation: "idle",
        scaleX: 0.36,
        scaleY: 0.36,
        x: 0,
        y: 0,
      });

      this._devilCharacterSpine.mixAnims("idle", "sleep_on", 0.3);
      this._devilCharacterSpine.mixAnims("idle_spin", "sleep_on", 0.3);
      this._devilCharacterSpine.mixAnims("sleep_on", "sleep_idle", 0.3);
      this._devilCharacterSpine.mixAnims("sleep_idle", "sleep_off", 0.3);
      this._devilCharacterSpine.mixAnims("idle", "big_win2", 0.3);
      this._devilCharacterSpine.mixAnims("big_win2", "big_win", 0.3);
      this._devilCharacterSpine.mixAnims("big_win2", "idle", 0.5);
      this._devilCharacterSpine.mixAnims("big_win", "idle", 0.5);
      this._devilCharacterSpine.mixAnims("idle", "point", 0.3);
      this._devilCharacterSpine.mixAnims("idle", "point_down", 0.3);
      this._devilCharacterSpine.mixAnims("idle", "defensive", 0.3);
      this._devilCharacterSpine.mixAnims("idle_spin", "defensive", 0.3);
      this._devilCharacterSpine.mixAnims("sleep_on", "point", 0.5);
      this._devilCharacterSpine.mixAnims("sleep_on", "point_down", 0.5);
      this._devilCharacterSpine.mixAnims("sleep_idle", "point", 0.5);
      this._devilCharacterSpine.mixAnims("sleep_idle", "point_down", 0.5);
      this._devilCharacterSpine.mixAnims("idle_spin", "point", 0.3);
      this._devilCharacterSpine.mixAnims("idle_spin", "point_down", 0.3);
      this._devilCharacterSpine.mixAnims("idle", "bubble_boost", 0.3);
      this._devilCharacterSpine.mixAnims("idle_spin", "bubble_boost", 0.3);
      this._devilCharacterSpine.mixAnims("sleep_idle", "bubble_boost", 0.5);

      // set up firewave animations
      const numReels = 5;
      for (let i = 0; i < numReels; i++) {
        const bubbleWaveAnim = resourceManager.createObject(
          "movie",
          `firewaveReel${i}`,
          `reelEffectsContainer:bubbleWaveReelContainer${i}`,
          {
            definition: "bubbleWave",
            anchor: 0.5,
            loop: false,
          }
        );
        bubbleWaveAnim.visible = false;
        bubbleWaveAnim.gotoAndStop(0);
        bubbleWaveAnim.onComplete = function () {
          bubbleWaveAnim.gotoAndStop(0);
          bubbleWaveAnim.visible = false;
        };
        this._bubbleWaveAnimations.push(bubbleWaveAnim);
      }

      this.idleLoop();
    }

    /**
     * Play fire wave anim
     */
    async fireWave() {
      const anim = systemData.isLandscape ? "point" : "point_down";
      this._devilCharacterSpine.play(anim, false, 0, () => this.idleLoop());

      await gsap.delayedCall(0.9);

      soundManager.play("fireWave");
      gameController.firewaveActive = true;
      gameController.injectCoinsAndGems();
      this._bubbleWaveAnimations.map((bubbleWaveAnim) => {
        bubbleWaveAnim.visible = true;
        bubbleWaveAnim.play();
      });
    }

    /**
     * Loop the sleep animation
     */
    sleepAnimation() {
      this.idle = true;
      this._devilCharacterSpine.play("sleep_on", false, 0, () => {
        this._devilCharacterSpine.play("sleep_idle", true);
      });
    }

    /**
     * Leave sleep animation
     */
    leaveSleepAnimation() {
      this.idle = false;
      this._devilCharacterSpine.play("sleep_off", false, 0, () => {
        this.idleLoop();
      });
    }

    /**
     * Play frustrated anim
     */
    frustrated() {
      musicManager.playDevilNegativeSound();
      this._devilCharacterSpine.play("defensive");
    }

    /**
     * Play idle loop anim
     */
    idleLoop() {
      this._devilCharacterSpine.play("idle", false, 0, () => {
        this._idleLoopCounter++;
        if (this._idleLoopCounter >= 2) {
          this.idleBlink();
        } else {
          this.idleLoop();
        }
      });
    }

    /**
     * Start big win and then loop dance
     */
    bigWinAnimation() {
      this._devilCharacterSpine.play("big_win2", true);
    }

    /**
     * Start big win and then loop dance
     */
    biggerWinAnimation() {
      this._devilCharacterSpine.play("big_win", true);
    }

    /**
     * Start big win and then loop dance
     */
    endBigWinAnimation() {
      this.idleLoop();
    }

    /**
     * Play idle blink anim
     */
    idleBlink() {
      this._idleLoopCounter = 0;
      this._devilCharacterSpine.play("idle_spin", false, 0, () => this.idleLoop());
    }

    /**
     * Play nudge anim when haven't landed devil symbol
     */
    async nudge() {
      musicManager.playDevilNegativeSound();

      const centreGlobalPos = resourceManager.get("slotContainer:reelContainer:reel7").getGlobalPosition();
      const localDevilShootEndPos = this._devilShootEmitterContainer.toLocal(centreGlobalPos);

      const animName = systemData.isLandscape ? "point" : "point_down";

      this._devilCharacterSpine.play("defensive", false, 0, () => {
        this._devilCharacterSpine.play(animName, false, 0, () => this.idleLoop());

        gsap.delayedCall(0.9, () => {
          soundManager.play("devilNudge");
        });
      });

      const animInfo = this._devilCharacterSpine.spineObject.spineData.animations.find(
        (anim) => anim.name === "defensive"
      );
      const animInfo2 = this._devilCharacterSpine.spineObject.spineData.animations.find(
        (anim) => anim.name === animName
      );
      const duration = animInfo.duration + animInfo2.duration;
      const delay = 1;

      await this.shootParticle(localDevilShootEndPos, duration, delay);
    }

    /**
     * Shoot the devil characer flame over to the collect meter
     */
    async shootToCollectMeter() {
      const animName = "point";

      this._devilCharacterSpine.play(animName, false, 0, () => this.idleLoop());

      gsap.delayedCall(0.2, () => {
        soundManager.play("devilFlamePoint");
      });

      const centreGlobalPos = resourceManager
        .get("collectMeterContainer:toCollectMeterContainer:textContainer:toCollectTextDisplay")
        .getGlobalPosition();
      const localDevilShootEndPos = this._devilShootEmitterContainer.toLocal(centreGlobalPos);

      const animInfo = this._devilCharacterSpine.spineObject.spineData.animations.find(
        (anim) => anim.name === animName
      );
      const duration = animInfo.duration;

      await this.shootParticle(localDevilShootEndPos, duration);
    }

    /**
     * Shoot particle to location
     */
    async shootParticle(locationToShoot, duration, delay = 0) {
      await new Promise((resolve) => {
        setTimeout(
          async () => {
            const emitterToPlay = this._bubbleShoot;
            emitterToPlay.visible = true;
            emitterToPlay.alpha = 0;
            emitterToPlay.scale.set(0);

            gsap.to(emitterToPlay, {
              duration: 0.15,
              delay,
              alpha: 1,
              pixi: { scale: 0.15 },
            });

            await gsap.to(emitterToPlay, {
              x: locationToShoot.x,
              y: locationToShoot.y,
              delay,
              duration: 0.6,
              onComplete: () => {
                emitterToPlay.visible = false;
                emitterToPlay.x = 0;
                emitterToPlay.y = 0;
              },
            });
            resolve();
          },
          duration * 0.4 * 1000
        );
      });
    }

    /**
     * Play lock wiggle animation
     */
    playLockWiggle() {
      this._devilCharacterSpine.play("wave", false, 0, () => this.idleLoop());
    }

    /**
     * Handle orientation change
     * @param {boolean} isLandscape
     */
    _orientationChange(isLandscape) {}
  }

  resourceManager.registerObjectType(
    "devilCharacter",
    () => new DevilCharacter(),
    initialise,
    () => []
  );
})();
